import React, { useMemo, useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import NappiSearch from "../Services/searchNappi";
import TariffSearch from "../Services/searchTariffs";

// Small modal behind Actions → Nappi. Search for a NAPPI item, then
// optionally link any tariff code to it. A linked NAPPI is submitted to the
// switch as ONE claim line under that tariff code (instead of the generic
// 0201 materials code) — no separate tariff line is needed on the invoice.
export default function AddNappiModal({
  show,
  onHide,
  invoiceLines = [],
  practiceNumber,
  onAdd,
}) {
  const [selectedNappi, setSelectedNappi] = useState(null);
  const [selectedTariff, setSelectedTariff] = useState(null);

  const isNappiLine = (l) => l?.type === "nappi" || !!l?.nappiCode;

  // Guard: the chosen tariff already exists as its own line on the invoice,
  // so linking it here would submit the same tariff code twice.
  const duplicateTariffLine = useMemo(() => {
    if (!selectedTariff?.code) return false;
    return (invoiceLines || []).some(
      (l) =>
        !isNappiLine(l) &&
        String(l.tariffCode || "") === String(selectedTariff.code),
    );
  }, [invoiceLines, selectedTariff]);

  const nappiRateLabel = useMemo(() => {
    const raw = selectedNappi?.rate;
    const n = typeof raw === "number" ? raw : parseFloat(raw);
    if (Number.isNaN(n)) return null;
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
    }).format(n);
  }, [selectedNappi]);

  const reset = () => {
    setSelectedNappi(null);
    setSelectedTariff(null);
  };

  const handleHide = () => {
    reset();
    onHide?.();
  };

  const handleAdd = () => {
    if (!selectedNappi) return;
    // Pass the full tariff object — the caller needs its description as
    // well as its code to compose the line.
    onAdd?.(selectedNappi, selectedTariff || null);
    reset();
    onHide?.();
  };

  return (
    <Modal show={show} onHide={handleHide} centered className="nappi-link-modal">
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Add NAPPI
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!selectedNappi ? (
          <Form.Group>
            <Form.Label>NAPPI item</Form.Label>
            <NappiSearch onNappiSelect={(n) => setSelectedNappi(n)} />
          </Form.Group>
        ) : (
          <div className="posted-invoice-info d-flex align-items-center gap-2 mb-3">
            <div className="flex-fill">
              <span className="fw-semibold">{selectedNappi.nappiCode}</span>
              <br />
              <small>{selectedNappi.name || selectedNappi.description}</small>
            </div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setSelectedNappi(null)}
            >
              <i className="fa-light fa-file-magnifying-glass me-2" />
              Change
            </Button>
          </div>
        )}

        <Form.Group className="mt-3">
          <Form.Label>Link a tariff code (optional)</Form.Label>
          {!selectedTariff ? (
            <>
              <TariffSearch
                practiceNumber={practiceNumber}
                onTariffCodeSelect={(t) => setSelectedTariff(t)}
              />
              <small className="text-muted">
                Unlinked NAPPIs are billed as material (0201). A linked NAPPI
                is claimed as one line under the tariff code.
              </small>
            </>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="text"
                size="sm"
                readOnly
                value={`${selectedTariff.code} — ${selectedTariff.description || ""}`}
                title={`${selectedTariff.code} — ${selectedTariff.description || ""}`}
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setSelectedTariff(null)}
              >
                <i className="fa-light fa-file-magnifying-glass me-2" />
                Change
              </Button>
            </div>
          )}
          {selectedTariff && (
            <small className="text-muted d-block mt-1">
              The line keeps the NAPPI rate
              {nappiRateLabel ? ` (${nappiRateLabel})` : ""} — it is NOT
              adjusted to the tariff rate. Edit the rate on the line if the
              arrangement price differs.
            </small>
          )}
        </Form.Group>

        {duplicateTariffLine && (
          <Alert variant="warning" className="mt-3 mb-0">
            Tariff <strong>{selectedTariff.code}</strong> is already a line on
            this invoice — linking it here will submit the same tariff code{" "}
            <strong>twice</strong> on the claim. Please{" "}
            <strong>remove that tariff line</strong> from the invoice after
            adding this NAPPI.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          style={{ minWidth: 120 }}
          onClick={handleAdd}
          disabled={!selectedNappi}
        >
          <i className="fa-light fa-crutches me-2" />
          Add NAPPI
        </Button>
        <Button
          variant="outline-secondary"
          style={{ minWidth: 120 }}
          onClick={handleHide}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
